import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  AlertCircle, 
  CheckCircle,
  ChevronDown,
  Upload,
  Package,
  Flame,
  X,
  Check,
  ArrowUpDown,
  MoreVertical
} from 'lucide-react';
import MaterialForm from './MaterialForm.jsx';
import MaterialDetails from './MaterialDetails.jsx';
import ExcelUpload from './ExcelUpload.jsx';
import Modal from '../Layout/Modal.jsx';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';
import { materialsAPI, uploadAPI } from '../../utils/api';
import { calculateUnitCostWithVat } from '../../utils/calculations';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useVat } from '../../contexts/VatContext';
import { useTheme } from '../../contexts/ThemeContext';

const CategoryMaterials = () => {
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { vatPercentage } = useVat();
  const { theme, isDark } = useTheme();
  const { categoryId: categoryIdParam } = useParams();
  const categoryId = parseInt(categoryIdParam, 10);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [showMaterialDetails, setShowMaterialDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  
  // Bulk delete state
  const [isBulkDeleteMode, setIsBulkDeleteMode] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState('id'); // 'id' or 'alphabetical'

  // Handle clicks outside category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown) {
        const dropdownElement = event.target.closest('.category-dropdown');
        if (!dropdownElement) {
          setShowCategoryDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  useEffect(() => {
    loadData();
  }, [categoryId]);
  
  // Refresh material data when VAT changes
  useEffect(() => {
    console.log('VAT changed to', vatPercentage, '- refreshing material data');
    loadData();
  }, [vatPercentage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
    };

    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [materialsData, categoriesData] = await Promise.all([
        materialsAPI.getByCategory(categoryId),
        materialsAPI.getCategories()
      ]);
      
      setMaterials(materialsData);
      setCategories(categoriesData);
      
      // Find current category
      const category = categoriesData.find(cat => cat.id === categoryId);
      setCurrentCategory(category);
    } catch (error) {
      console.error('Error loading data:', error);
      setErrorMessage('Failed to load materials data');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMaterial = async (materialData) => {
    console.log('handleSaveMaterial called with data:', materialData);
    try {
      if (editingMaterial) {
        console.log('Updating existing material:', editingMaterial.id);
        // Update existing material
        const updatedMaterial = await materialsAPI.update(editingMaterial.id, materialData);
        setMaterials(materials.map(material => 
          material.id === editingMaterial.id ? updatedMaterial : material
        ));
        setSuccessMessage(t('messages.materialSaved'));
      } else {
        console.log('Creating new material');
        // Add new material
        const newMaterial = await materialsAPI.create(materialData);
        console.log('New material created:', newMaterial);
        // Only add to local state if the material belongs to the current category
        if (newMaterial.category_id === categoryId) {
          setMaterials([...materials, newMaterial]);
        }
        setSuccessMessage(t('messages.materialSaved'));
      }
      
      setShowMaterialForm(false);
      setEditingMaterial(null);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving material:', error);
      setErrorMessage('Failed to save material');
      setShowErrorModal(true);
    }
  };

  // Function to extract public ID from Cloudinary URL
  const extractPublicIdFromUrl = (url) => {
    if (!url) return null;
    try {
      // Extract public ID from Cloudinary URL
      // Format: https://res.cloudinary.com/cloud_name/image/upload/v123456789/folder/image.jpg
      const match = url.match(/\/upload\/[^\/]+\/(.+)$/);
      if (match) {
        let publicId = match[1];
        // Remove file extension from public ID (Cloudinary public IDs don't include extensions)
        publicId = publicId.replace(/\.[^/.]+$/, '');
        return publicId;
      }
      return null;
    } catch (error) {
      console.error('Error extracting public ID from URL:', error);
      return null;
    }
  };

  // Function to clean up material image from Cloudinary
  const cleanupMaterialImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const imagePublicId = extractPublicIdFromUrl(imageUrl);
      if (imagePublicId) {
        console.log('🧹 Cleanup: Deleting material image:', imagePublicId);
        await uploadAPI.deleteImage(imagePublicId);
        console.log('✅ Cleanup: Material image cleaned up successfully');
      }
    } catch (error) {
      console.error('❌ Cleanup: Failed to cleanup material image:', error);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    const materialRecord = materials.find(m => m.id === materialId);
    setMaterialToDelete(materialRecord || null);

    setConfirmAction(() => async () => {
      try {
        // Find the material to get its image URL before deletion
        const materialToDeleteRecord = materials.find(m => m.id === materialId);
        
        // Delete the material from database
        await materialsAPI.delete(materialId);
        
        // Update UI immediately for responsive experience
        setMaterials(materials.filter(material => material.id !== materialId));
        
        // Update current category material count after delete
        if (currentCategory) {
          setCurrentCategory(prev => ({
            ...prev,
            material_count: Math.max(0, (prev.material_count || 0) - 1)
          }));
        }
        
        setSuccessMessage(t('messages.materialDeleted'));
        setShowSuccessModal(true);
        
        // Clean up the material's image from Cloudinary in the background
        if (materialToDeleteRecord?.image_url) {
          cleanupMaterialImage(materialToDeleteRecord.image_url).catch(error => {
            console.error('Background cleanup failed:', error);
          });
        }
      } catch (error) {
        console.error('Error deleting material:', error);
        if (error.message && error.message.includes('Cannot delete material that is being used in recipes')) {
          const match = error.message.match(/used in\s+(\d+)\s+recipe/i);
          if (match?.[1]) {
            setErrorMessage(t('messages.cannotDeleteMaterialUsedInRecipesWithCount', { count: Number(match[1]) }));
          } else {
            setErrorMessage(t('messages.cannotDeleteMaterialUsedInRecipes'));
          }
        } else {
          setErrorMessage(t('messages.failedDeleteMaterial'));
        }
        setShowErrorModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  // Bulk delete functions
  const handleBulkDeleteMode = () => {
    setIsBulkDeleteMode(true);
    setSelectedMaterials(new Set());
  };

  const handleCancelBulkDelete = () => {
    setIsBulkDeleteMode(false);
    setSelectedMaterials(new Set());
  };

  const handleMaterialSelection = (materialId) => {
    const newSelected = new Set(selectedMaterials);
    if (newSelected.has(materialId)) {
      newSelected.delete(materialId);
    } else {
      newSelected.add(materialId);
    }
    setSelectedMaterials(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedMaterials.size === filteredMaterials.length) {
      setSelectedMaterials(new Set());
    } else {
      setSelectedMaterials(new Set(filteredMaterials.map(material => material.id)));
    }
  };

  const handleBulkDelete = async () => {
    setConfirmAction(() => async () => {
      try {
        const selectedMaterialIds = Array.from(selectedMaterials);
        
        // First, collect all materials to be deleted with their image URLs
        const materialsToDelete = materials.filter(m => selectedMaterialIds.includes(m.id));
        
        const results = await Promise.allSettled(
          selectedMaterialIds.map(materialId => materialsAPI.delete(materialId))
        );
        
        // Track successful and failed deletions
        const successfulDeletions = [];
        const failedDeletions = [];
        
        results.forEach((result, index) => {
          const materialId = selectedMaterialIds[index];
          const material = materials.find(m => m.id === materialId);
          
          if (result.status === 'fulfilled') {
            successfulDeletions.push(material);
          } else {
            // Check if it's a "used in recipes" error
            const errorMessage = result.reason?.message || 'Unknown error';
            if (errorMessage.includes('Cannot delete material that is being used in recipes')) {
              failedDeletions.push({
                material,
                reason: 'used in recipes'
              });
            } else {
              failedDeletions.push({
                material,
                reason: 'other error'
              });
            }
          }
        });
        
        // Update materials list (remove successfully deleted ones) immediately
        setMaterials(materials.filter(material => 
          !successfulDeletions.some(deleted => deleted.id === material.id)
        ));
        
        // Clean up images for successfully deleted materials in the background
        for (const material of successfulDeletions) {
          if (material?.image_url) {
            cleanupMaterialImage(material.image_url).catch(error => {
              console.error('Background cleanup failed:', error);
            });
          }
        }

        // Update current category material count after bulk delete
        if (successfulDeletions.length > 0 && currentCategory) {
          setCurrentCategory(prev => ({
            ...prev,
            material_count: Math.max(0, (prev.material_count || 0) - successfulDeletions.length)
          }));
        }
        
        // Clear selection and exit bulk delete mode
        setSelectedMaterials(new Set());
        setIsBulkDeleteMode(false);
        
        // Show appropriate message based on results
        if (successfulDeletions.length > 0 && failedDeletions.length === 0) {
          // All deletions successful
          setSuccessMessage(t('messages.bulkMaterialsDeleted', { count: successfulDeletions.length }));
          setShowSuccessModal(true);
        } else if (successfulDeletions.length > 0 && failedDeletions.length > 0) {
          // Partial success
          const usedInRecipes = failedDeletions.filter(f => f.reason === 'used in recipes');
          const otherErrors = failedDeletions.filter(f => f.reason === 'other error');
          
          let message = t('messages.bulkMaterialsDeletedPartial', { count: successfulDeletions.length });
          
          if (usedInRecipes.length > 0) {
            message += ` ${t('messages.bulkMaterialsBlockedInRecipes', { count: usedInRecipes.length })}`;
          }
          
          if (otherErrors.length > 0) {
            message += ` ${t('messages.bulkMaterialsOtherErrors', { count: otherErrors.length })}`;
          }
          
          setSuccessMessage(message);
          setShowSuccessModal(true);
        } else {
          // All deletions failed
          const usedInRecipes = failedDeletions.filter(f => f.reason === 'used in recipes');
          const otherErrors = failedDeletions.filter(f => f.reason === 'other error');
          
          let errorMsg = `${t('messages.bulkMaterialsDeleteFailedPrefix')} `;
          if (usedInRecipes.length > 0) {
            errorMsg += t('messages.bulkMaterialsInRecipes', { count: usedInRecipes.length });
          }
          if (otherErrors.length > 0) {
            errorMsg += ` ${t('messages.bulkMaterialsOtherFailed', { count: otherErrors.length })}`;
          }
          
          setErrorMessage(errorMsg);
          setShowErrorModal(true);
        }
      } catch (error) {
        console.error('Error in bulk delete operation:', error);
        setErrorMessage(t('messages.bulkDeleteUnexpected'));
        setShowErrorModal(true);
      }
    });
    setShowBulkDeleteConfirm(true);
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setShowMaterialForm(true);
  };

  const handleViewMaterial = (material) => {
    setSelectedMaterial(material);
    setShowMaterialDetails(true);
  };

  const handleCategoryChange = (newCategoryId) => {
    navigate(`/materials/category/${newCategoryId}`);
  };

  const handleExcelUpload = async (uploadedData) => {
    setIsUploading(true);
    try {
      // First, collect all unique category names that need to be created
      const categoriesToCreate = new Map();
      for (const materialData of uploadedData) {
        if (materialData.categoryName && !materialData.categoryId) {
          const categoryName = materialData.categoryName.trim();
          if (!categoriesToCreate.has(categoryName)) {
            categoriesToCreate.set(categoryName, []);
          }
          categoriesToCreate.get(categoryName).push(materialData);
        }
      }

      // Create categories and store their IDs
      const categoryIdMap = new Map();
      const newCategories = [];
      for (const [categoryName, materials] of categoriesToCreate) {
        try {
          const newCategory = await materialsAPI.createCategory({
            name_en: categoryName,
            name_ar: categoryName // You might want to add Arabic name handling
          });
          categoryIdMap.set(categoryName, newCategory.id);
          newCategories.push(newCategory);
          
          // Update all materials that use this category
          materials.forEach(material => {
            material.categoryId = newCategory.id;
          });
        } catch (error) {
          console.error('Error creating category:', error);
          // Use default category (ID 0) if creation fails
          categoryIdMap.set(categoryName, 0);
          materials.forEach(material => {
            material.categoryId = 0;
          });
        }
      }

      // Update local categories state with newly created categories
      if (newCategories.length > 0) {
        setCategories(prev => [...prev, ...newCategories]);
      }

      // Now create all materials
      const createdMaterials = [];
      for (const materialData of uploadedData) {
        const newMaterial = await materialsAPI.create({
          name_en: materialData.nameEn,
          name_ar: materialData.nameAr,
          brand_name: materialData.brandName,
          main_unit: materialData.mainUnit,
          quantity_per_main_unit: materialData.quantityPerMainUnit,
          quantity_per_piece: materialData.quantityPerPiece,
          sub_unit: materialData.subUnit,
          // main_price field removed - using price_with_vat instead
          price_with_vat: materialData.priceWithVat,
          price_without_vat: materialData.priceWithoutVat,
          supplier: materialData.supplier,
          calories: materialData.calories,
          serving_size: materialData.servingSize,
          category_id: materialData.categoryId !== undefined ? materialData.categoryId : categoryId
        });

        createdMaterials.push(newMaterial);
        // Only add to local state if the material belongs to the current category
        if (newMaterial.category_id === categoryId) {
          setMaterials(prev => [...prev, newMaterial]);
        }
      }

      // Update category material counts
      if (createdMaterials.length > 0) {
        setCategories(prev => prev.map(category => {
          const materialsInCategory = createdMaterials.filter(material => material.category_id === category.id);
          if (materialsInCategory.length > 0) {
            return {
              ...category,
              material_count: (category.material_count || 0) + materialsInCategory.length
            };
          }
          return category;
        }));
      }

      setSuccessMessage(t('materials.successfullyUploaded', { count: uploadedData.length }));
      setShowSuccessModal(true);
      setShowExcelUpload(false);
    } catch (error) {
      console.error('Error uploading materials:', error);
      setShowExcelUpload(false);
      const rawReason = error?.message?.trim();
      const reason = rawReason === 'Failed to create material'
        ? t('messages.failedCreateMaterial')
        : rawReason;
      setErrorMessage(
        reason
          ? t('messages.failedUploadMaterialsWithReason', { reason })
          : t('messages.failedUploadMaterials')
      );
      setShowErrorModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  // Sort and filter materials for display
  const getSortedMaterials = (materialsList) => {
    return materialsList.sort((a, b) => {
      if (sortOrder === 'id') {
        return a.id - b.id;
      } else {
        // alphabetical
        return (a.name_en || '').localeCompare(b.name_en || '');
      }
    });
  };

  // Filter materials for display (always filter based on search term)
  const filteredMaterials = getSortedMaterials(
    materials.filter(material => 
      material.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.id?.toString().includes(searchTerm)
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading materials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-background text-foreground min-h-screen p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('materials.title')}</h1>
            <p className="mt-2 text-muted-foreground">
              {i18n.language === 'ar' 
                ? `${t('common.materials')} ${t('common.in')} ${t('common.category')} ${currentCategory?.name_ar || currentCategory?.name_en || t('materials.unnamed')}`
                : `${t('common.materials')} ${t('common.in')} ${currentCategory?.name_en || t('materials.unnamed')} ${t('common.category')}`
              }
            </p>
        </div>
        
        {/* Action Buttons */}
        <div className={`flex justify-end mt-4 sm:mt-0 ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          {isBulkDeleteMode ? (
            <>
              <button
                onClick={handleCancelBulkDelete}
                className="px-4 py-2 text-sm font-medium text-card-foreground bg-secondary border border-border rounded-md hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-center"
              >
                <X className="h-4 w-4 mr-2" />
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 text-sm font-medium text-card-foreground bg-secondary border border-border rounded-md hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-center"
              >
                <Check className="h-4 w-4 mr-2" />
                {selectedMaterials.size === filteredMaterials.length ? t('common.deselectAll') : t('common.selectAll')}
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={selectedMaterials.size === 0}
                className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive border border-destructive rounded-md hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('common.deleteSelected')} ({selectedMaterials.size})
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowMaterialForm(true)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-foreground bg-primary border-2 border-primary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                title="Add new material"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('materials.addNew')}
              </button>
              <button
                onClick={() => setShowExcelUpload(true)}
                className="px-3 py-2 text-sm font-medium text-card-foreground bg-secondary border-2 border-secondary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                title="Import materials from Excel or export existing materials"
              >
                <Upload className="h-4 w-4" />
              </button>
              <button
                onClick={handleBulkDeleteMode}
                className="px-3 py-2 text-sm font-medium text-destructive-foreground bg-destructive border-2 border-destructive rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                title={t('common.bulkDelete')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/materials')}
          className="inline-flex items-center justify-center px-3 py-2 border border-muted rounded-lg text-sm font-medium text-muted-foreground bg-muted hover:bg-muted/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-muted transition-colors"
          title="Back to Categories"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        
        {/* Category Dropdown */}
        <div className="relative category-dropdown">
          <button
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="inline-flex items-center px-4 py-2 border border-muted rounded-lg text-sm font-medium text-muted-foreground bg-muted hover:bg-muted/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-muted transition-colors min-w-[200px] justify-between"
          >
            <span>{i18n.language === 'ar' 
              ? (currentCategory?.name_ar || currentCategory?.name_en || t('common.selectCategory'))
              : (currentCategory?.name_en || t('common.selectCategory'))
            }</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {showCategoryDropdown && (
            <div className="absolute top-full left-0 mt-1 w-full bg-card border border-border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    handleCategoryChange(category.id);
                    setShowCategoryDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2 hover:bg-muted transition-colors ${
                    category.id === parseInt(categoryId) ? 'bg-primary/10 text-primary' : 'text-card-foreground'
                  }`}
                  title={i18n.language === 'ar' ? (category.name_ar || category.name_en) : category.name_en}
                >
                  <span className="truncate block">
                    {i18n.language === 'ar' ? (category.name_ar || category.name_en) : category.name_en}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder={t('common.searchByMaterialNameOrId')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent pl-10"
              style={{ paddingLeft: '2.75rem' }}
            />
          </div>
        </div>

        {/* Sort Button */}
        <button
          onClick={(e) => {
            setSortOrder(sortOrder === 'id' ? 'alphabetical' : 'id');
            e.target.blur(); // Remove focus after click
          }}
          className="inline-flex items-center justify-center h-10 px-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
          title={`Sort by ${sortOrder === 'id' ? 'alphabetical' : 'ID'} order`}
        >
          <ArrowUpDown className="h-4 w-4" />
        </button>
      </div>

      {/* Materials Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-card-foreground">
            {i18n.language === 'ar' 
              ? `${t('materials.materials')} ${currentCategory?.name_ar || currentCategory?.name_en || ''}`
              : `${currentCategory?.name_en || 'Materials'} ${t('materials.materials')}`
            }
          </h3>
          <div className="text-sm text-muted-foreground">
            {t('common.materialsTotal')} {filteredMaterials.length}
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
         {filteredMaterials.map((material) => (
           <div
             key={material.id}
             onClick={() => !isBulkDeleteMode && handleViewMaterial(material)}
             className={`group ${isBulkDeleteMode ? 'cursor-default' : 'cursor-pointer'} card-container`}
           >
             <div className={`bg-card rounded-2xl border border-border transition-all duration-300 transform overflow-hidden flex flex-col h-full ${
               isBulkDeleteMode && selectedMaterials.has(material.id)
                 ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                 : 'hover:-translate-y-1'
             }`}>
                {/* Selection Checkbox */}
                {isBulkDeleteMode && (
                  <div className="absolute top-3 right-3 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMaterialSelection(material.id);
                      }}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        selectedMaterials.has(material.id)
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'bg-card border-border hover:border-border'
                      }`}
                    >
                      {selectedMaterials.has(material.id) && (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                )}

                {/* Actions Dropdown */}
                {!isBulkDeleteMode && (
                  <div className="absolute top-3 right-3 z-10">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownId(openDropdownId === material.id ? null : material.id);
                        }}
                        className="w-8 h-8 rounded-full border border-border hover:border-border flex items-center justify-center transition-all hover:bg-muted dark:hover:bg-muted bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]"
                      >
                        <MoreVertical className="h-4 w-4 text-muted-foreground" />
                      </button>
                      
                      {openDropdownId === material.id && (
                        <div className="absolute right-0 top-9 bg-card rounded-lg shadow-lg border border-border py-1 min-w-[120px] z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditMaterial(material);
                              setOpenDropdownId(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-card-foreground hover:bg-muted flex items-center gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteMaterial(material.id);
                              setOpenDropdownId(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-muted flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            {t('common.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Material Image Header */}
                <div className="relative h-28 bg-input flex items-center justify-center overflow-hidden border-b border-border">
                  {material.image_url ? (
                    <img
                      src={material.image_url}
                      alt={material.name_en}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                      <div className="w-full h-full bg-card flex items-center justify-center">
                        <Package className="h-16 w-16 text-border" />
                      </div>
                  )}
                  
                  {/* Material ID Badge */}
                  <div className={`absolute top-3 px-2 py-1 rounded-full text-xs font-medium text-muted-foreground bg-muted/50 dark:bg-[rgba(10,18,25,0.5)] ${i18n.language === 'ar' ? 'left-3' : 'left-3'}`}>
                    {material.id}
                  </div>
                  
                  {/* Calories Badge */}
                  <div className={`absolute bottom-3 px-2 py-1 rounded-full text-xs font-medium text-orange-600 flex items-center gap-1 bg-muted/50 dark:bg-[rgba(10,18,25,0.5)] ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`}>
                    <Flame className="h-3 w-3 text-orange-500" />
                    {material.calories ? Math.round(material.calories) : '-'}kcal/{material.serving_size || '-'}
                  </div>
                  
                  {/* View Details Overlay */}
                  {!isBulkDeleteMode && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-card px-4 py-2 rounded-lg text-sm font-medium text-card-foreground">
                        {t('common.clickToViewDetails')}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Content Section */}
                <div className="p-2 space-y-1 flex-1">
                  {/* Material Names */}
                  <div className="text-center space-y-1 -mt-2 border-b border-border pb-2 -mx-2 px-2">
                    <h3 className="font-bold text-card-foreground text-lg group-hover:text-primary transition-colors truncate" title={material.name_en}>
                      {material.name_en}
                    </h3>
                    {material.name_ar && (
                      <p className="text-sm text-muted-foreground font-arabic truncate" title={material.name_ar}>
                        {material.name_ar}
                      </p>
                    )}
                  </div>

                  {/* Allergen Section */}
                  <div className="min-h-[24px] flex justify-center items-center">
                    {material.allergens && material.allergens.length > 0 ? (
                      <AllergenBadge 
                        allergens={material.allergens}
                        variant="warning"
                        size="xs"
                        showCount={true}
                        maxDisplay={2}
                      />
                    ) : (
                      <div className="text-xs text-muted-foreground">{t('common.noAllergens')}</div>
                    )}
                  </div>
                  
                </div>
                
                {/* Unit Cost */}
                <div className="p-2 text-center -mx-3 bg-[#B3E8F2] dark:bg-[#2A4A5A]">
                  <div className="mb-1">
                    <span className="text-xs text-card-foreground font-medium uppercase tracking-wide">{t('materials.unitCost')}</span>
                  </div>
                  <div className="text-sm font-bold text-card-foreground">
                    {formatCurrency(calculateUnitCostWithVat(material, vatPercentage))}
                  </div>
                  <div className="text-xs text-card-foreground">
                    {t('materials.includesVat')}
                  </div>
                </div>
                
              </div>
           </div>
         ))}
       </div>
      </div>

      {/* Empty State */}
      {filteredMaterials.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-card-foreground mb-2">
            {searchTerm ? 'No materials found' : t('materials.noMaterialsInCategory')}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : t('materials.addFirstMaterial')
            }
          </p>
        </div>
      )}

      {/* Material Form Modal */}
      {showMaterialForm && (
        <MaterialForm
          material={editingMaterial}
          categories={categories}
          currentCategoryId={categoryId}
          onSave={handleSaveMaterial}
          onCancel={() => {
            setShowMaterialForm(false);
            setEditingMaterial(null);
          }}
        />
      )}

      {/* Material Details Modal */}
      {showMaterialDetails && selectedMaterial && (
        <MaterialDetails
          material={selectedMaterial}
          onClose={() => {
            setShowMaterialDetails(false);
            setSelectedMaterial(null);
          }}
        />
      )}

      {/* Error Modal */}
      <Modal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.error')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-muted-foreground">{errorMessage}</p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowErrorModal(false)}
            className="btn btn-primary"
          >
            {t('common.ok')}
          </button>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => {
        setShowConfirmModal(false);
        setMaterialToDelete(null);
      }}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.deleteMaterial')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-card-foreground">{t('common.confirmDeleteMaterial', { name: materialToDelete?.name_en || '' })}</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setShowConfirmModal(false);
              setMaterialToDelete(null);
            }}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={async () => {
              setShowConfirmModal(false);
              setMaterialToDelete(null);
              if (confirmAction) {
                await confirmAction();
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200"
          >
            {t('common.deleteMaterial')}
          </button>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={showBulkDeleteConfirm} onClose={() => setShowBulkDeleteConfirm(false)}>
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">{t('common.confirmBulkDelete')}</h2>
          <p className="text-card-foreground">
            {t('common.confirmDeleteSelectedMaterials', { count: selectedMaterials.size })}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowBulkDeleteConfirm(false)}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={async () => {
              setShowBulkDeleteConfirm(false);
              if (confirmAction) {
                await confirmAction();
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200"
          >
            {t('common.deleteSelectedMaterialsCount', { count: selectedMaterials.size })}
          </button>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <div className="flex items-center mb-6">
          <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.success')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowSuccessModal(false)}
            className="btn btn-primary"
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Excel Import/Export Modal */}
      {showExcelUpload && (
        <ExcelUpload
          categories={categories}
          onUpload={handleExcelUpload}
          onCancel={() => setShowExcelUpload(false)}
          isUploading={isUploading}
        />
      )}
    </div>
  );
};

export default CategoryMaterials; 